import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Receipt, Upload, X, Plus, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { useCreateCleanerPurchaseMulti, type CleanerPurchaseDeliveryStatus } from "@/hooks/useCleanerPurchases";
import { useInventoryForCleaner } from "@/hooks/useCleaner";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AddInventoryItemSheet } from "@/components/inventory/AddInventoryItemSheet";

const RECEIPT_TOTAL_TOLERANCE_CENTS = 5;
type ReceiptTotalStatus = "matched" | "mismatch" | "unknown";

interface LineItemRow {
  id: string;
  inventory_item_id: string;
  quantity: number;
}

interface MultiItemPurchaseSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  cleanerId: string;
  cleaningTaskId?: string | null;
  scheduledDate?: string | null;
}

export function MultiItemPurchaseSheet({
  open,
  onOpenChange,
  propertyId,
  cleanerId,
  cleaningTaskId,
  scheduledDate,
}: MultiItemPurchaseSheetProps) {
  const { tDynamic } = useTranslation();
  const { data: inventoryItems = [] } = useInventoryForCleaner(propertyId);
  const createMulti = useCreateCleanerPurchaseMulti();

  const [totalAmount, setTotalAmount] = useState("");
  const [deliveryStatus, setDeliveryStatus] = useState<CleanerPurchaseDeliveryStatus>("delivered");
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptUploadedUrl, setReceiptUploadedUrl] = useState<string | null>(null);
  const [receiptAnalysis, setReceiptAnalysis] = useState<{
    total_cents: number;
    currency: string;
    confidence: number;
    source: string;
  } | null>(null);
  const [receiptAnalysisLoading, setReceiptAnalysisLoading] = useState(false);
  const [lineItems, setLineItems] = useState<LineItemRow[]>(() => [{ id: crypto.randomUUID(), inventory_item_id: "", quantity: 1 }]);
  const [uploading, setUploading] = useState(false);
  const [addItemSheetOpen, setAddItemSheetOpen] = useState(false);
  /** When set, the Add Item sheet is open to create an item for this line row id; on created we set this row's inventory_item_id. */
  const [addItemForRowId, setAddItemForRowId] = useState<string | null>(null);
  const totalInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setTotalAmount("");
    setDeliveryStatus("delivered");
    setReceiptFile(null);
    setReceiptUploadedUrl(null);
    setReceiptAnalysis(null);
    setLineItems([{ id: crypto.randomUUID(), inventory_item_id: "", quantity: 1 }]);
    setAddItemSheetOpen(false);
    setAddItemForRowId(null);
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  const uploadReceipt = async (file: File): Promise<string | null> => {
    const fileExt = file.name.split(".").pop();
    const fileName = `${propertyId}/${cleanerId}/multi/${Date.now()}.${fileExt}`;
    const { error } = await supabase.storage.from("cleaning-photos").upload(fileName, file);
    if (error) return null;
    const { data: urlData } = supabase.storage.from("cleaning-photos").getPublicUrl(fileName);
    return urlData.publicUrl;
  };

  useEffect(() => {
    if (!open) return;
    reset();
  }, [open]);

  useEffect(() => {
    if (!receiptFile) {
      setReceiptUploadedUrl(null);
      setReceiptAnalysis(null);
      return;
    }
    const timer = setTimeout(async () => {
      setReceiptAnalysisLoading(true);
      setReceiptAnalysis(null);
      try {
        const url = await uploadReceipt(receiptFile);
        if (url) {
          setReceiptUploadedUrl(url);
          const { data, error } = await supabase.functions.invoke("analyze-receipt-total", {
            body: { receiptUrl: url },
          });
          if (!error && data?.total_cents != null) {
            setReceiptAnalysis({
              total_cents: data.total_cents,
              currency: data.currency ?? "USD",
              confidence: typeof data.confidence === "number" ? data.confidence : 0.5,
              source: data.source ?? "ai",
            });
          }
        }
      } catch {
        setReceiptAnalysis(null);
      } finally {
        setReceiptAnalysisLoading(false);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [receiptFile?.name, receiptFile?.size]);

  const enteredAmountCents = (() => {
    const a = parseFloat(totalAmount);
    return Number.isFinite(a) && a >= 0 ? Math.round(a * 100) : null;
  })();
  const receiptTotalStatus: ReceiptTotalStatus = (() => {
    if (!receiptAnalysis) return "unknown";
    if (receiptAnalysis.confidence < 0.5) return "unknown";
    if (enteredAmountCents == null) return "unknown";
    const diff = Math.abs(enteredAmountCents - receiptAnalysis.total_cents);
    return diff <= RECEIPT_TOTAL_TOLERANCE_CENTS ? "matched" : "mismatch";
  })();

  const addLine = () => {
    setLineItems((prev) => [...prev, { id: crypto.randomUUID(), inventory_item_id: "", quantity: 1 }]);
  };

  const removeLine = (id: string) => {
    setLineItems((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  };

  const updateLine = (id: string, patch: Partial<LineItemRow>) => {
    setLineItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const handleSubmit = async () => {
    const amount = parseFloat(totalAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast({ title: tDynamic("Enter total paid"), variant: "destructive" });
      return;
    }
    if (!receiptFile) {
      toast({ title: tDynamic("Receipt required"), variant: "destructive" });
      return;
    }
    const validLines = lineItems.filter(
      (r) => r.inventory_item_id && r.quantity >= 1
    );
    if (validLines.length === 0) {
      toast({ title: tDynamic("Add at least one item with quantity"), variant: "destructive" });
      return;
    }

    setUploading(true);
    try {
      const receiptUrl = receiptUploadedUrl || (await uploadReceipt(receiptFile)) || undefined;
      if (!receiptUrl) {
        toast({ title: tDynamic("Failed to upload receipt"), variant: "destructive" });
        return;
      }
      await createMulti.mutateAsync({
        propertyId,
        cleanerId,
        amount,
        receiptUrl,
        cleaningTaskId: cleaningTaskId ?? undefined,
        scheduledDate: scheduledDate ?? undefined,
        deliveryStatus,
        lineItems: validLines.map((r) => ({
          inventory_item_id: r.inventory_item_id,
          quantity: r.quantity,
        })),
        receiptTotalCents: receiptAnalysis?.total_cents ?? null,
        receiptTotalConfidence: receiptAnalysis?.confidence ?? null,
        receiptTotalStatus,
        receiptTotalSource: receiptAnalysis?.source ?? null,
      });
      toast({ title: tDynamic("Purchase submitted") });
      handleClose();
    } catch (e) {
      toast({ title: tDynamic("Error"), description: (e as Error).message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side="bottom" className="h-auto max-h-[90vh] rounded-t-xl overflow-y-auto">
        <SheetHeader className="text-left pb-2">
          <SheetTitle>{tDynamic("One receipt (multiple items)")}</SheetTitle>
        </SheetHeader>

        <div className="space-y-4 pb-6">
          <div>
            <Label>{tDynamic("Receipt photo")} *</Label>
            <div className="mt-1.5">
              {receiptFile ? (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <Receipt className="w-5 h-5 text-muted-foreground" />
                  <span className="flex-1 truncate text-sm">{receiptFile.name}</span>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setReceiptFile(null)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 h-12 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50">
                  <Upload className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{tDynamic("Upload receipt")}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                  />
                </label>
              )}
            </div>
            {receiptAnalysisLoading && (
              <p className="text-xs text-muted-foreground mt-1.5">{tDynamic("Detecting receipt total...")}</p>
            )}
            {!receiptAnalysisLoading && receiptAnalysis && (
              <div className="mt-2 p-2.5 rounded-lg bg-muted/60 text-sm">
                <p className="font-medium">
                  {tDynamic("Receipt total detected")}: ${(receiptAnalysis.total_cents / 100).toFixed(2)}
                  {receiptAnalysis.confidence < 1 && (
                    <span className="text-muted-foreground font-normal ml-1">
                      ({Math.round(receiptAnalysis.confidence * 100)}%)
                    </span>
                  )}
                </p>
                {receiptTotalStatus === "matched" && (
                  <p className="flex items-center gap-1.5 text-green-700 mt-1">
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                    {tDynamic("Matched")}
                  </p>
                )}
                {receiptTotalStatus === "mismatch" && (
                  <div className="mt-2 space-y-1.5">
                    <p className="flex items-center gap-1.5 text-amber-700">
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      {tDynamic("Receipt total doesn't match entered amount.")}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => totalInputRef.current?.focus()}
                      >
                        {tDynamic("Edit amount")}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => setTotalAmount((receiptAnalysis.total_cents / 100).toFixed(2))}
                      >
                        {tDynamic("Use detected total")}
                      </Button>
                    </div>
                  </div>
                )}
                {receiptTotalStatus === "unknown" && receiptAnalysis.confidence < 0.5 && (
                  <p className="text-amber-700 mt-1 text-xs">
                    {tDynamic("Couldn't confidently detect total. This will require owner approval.")}
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <Label>{tDynamic("Total paid ($)")} *</Label>
            <Input
              ref={totalInputRef}
              type="number"
              step="0.01"
              min="0"
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
              placeholder="0.00"
              className="mt-1.5 h-12"
            />
          </div>

          <div>
            <Label>{tDynamic("Purchase status")}</Label>
            <Select
              value={deliveryStatus}
              onValueChange={(v) => setDeliveryStatus(v as CleanerPurchaseDeliveryStatus)}
            >
              <SelectTrigger className="mt-1.5 h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="delivered">{tDynamic("In stock")}</SelectItem>
                <SelectItem value="ordered">{tDynamic("Ordered (not arrived)")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>{tDynamic("Items")}</Label>
              <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onClick={addLine}>
                <Plus className="w-4 h-4 mr-1" />
                {tDynamic("Add item")}
              </Button>
            </div>
            <div className="space-y-2 mt-2">
              {lineItems.map((row) => (
                <div key={row.id} className="flex gap-2 items-center">
                  <Select
                    value={row.inventory_item_id || "_none"}
                    onValueChange={(v) => {
                      if (v === "__add_new__") {
                        setAddItemForRowId(row.id);
                        setAddItemSheetOpen(true);
                        return;
                      }
                      updateLine(row.id, { inventory_item_id: v === "_none" ? "" : v });
                    }}
                  >
                    <SelectTrigger className="flex-1 min-w-0 h-10">
                      <SelectValue placeholder={tDynamic("Select item")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">{tDynamic("— Select —")}</SelectItem>
                      <SelectItem value="__add_new__" className="font-medium text-primary">
                        <Plus className="w-4 h-4 mr-2 inline" />
                        {tDynamic("Add new inventory item")}
                      </SelectItem>
                      {inventoryItems.map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {tDynamic(item.item_name)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min={1}
                    value={row.quantity}
                    onChange={(e) => updateLine(row.id, { quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                    className="w-20 h-10 text-center"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 shrink-0"
                    onClick={() => removeLine(row.id)}
                    disabled={lineItems.length <= 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={handleClose} className="flex-1">
              {tDynamic("Cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={createMulti.isPending || uploading || !receiptFile || !totalAmount || lineItems.every((r) => !r.inventory_item_id)}
              className="flex-1"
            >
              {uploading || createMulti.isPending ? tDynamic("Submitting...") : tDynamic("Submit")}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>

    <AddInventoryItemSheet
      open={addItemSheetOpen}
      onOpenChange={(open) => {
        if (!open) setAddItemForRowId(null);
        setAddItemSheetOpen(open);
      }}
      propertyId={propertyId}
      onCreated={(item) => {
        if (addItemForRowId) updateLine(addItemForRowId, { inventory_item_id: item.id });
      }}
    />
  </>
  );
}
